import React, { useEffect, useState, useMemo } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import usePageTitle from '../../hooks/usePageTitle';
import { SignaturesList } from '../../components/SignaturesList';
import { SignaturesGraph } from '../../components/SignaturesGraph';
import Analytics from '../../components/Analytics';
import { SignaturesByAccount } from '../../components/SignaturesByAccount';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { usePageHeader } from '../../contexts/PageHeaderContext';

export const CONTAINER_CONTENT_CLASS = "flex flex-wrap items-center lg:items-end justify-between pb-5"

interface SignaturesRoutesProps {
    viewType?: 'list' | 'dashboard' | 'analytics' | 'report';
}

export function SignaturesRoutes({ viewType = 'list' }: SignaturesRoutesProps) {
    const { copy } = useLocalizedCopy();
    const location = useLocation();
    const { setPageHeader } = usePageHeader();
    const { viewingAllAccounts } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');

    usePageTitle(copy.Services.signatures.title);

    const isListView = viewType === 'list';
    const isDashboardView = viewType === 'dashboard';
    const isAnalyticsView = viewType === 'analytics';
    const isReportView = viewType === 'report';

    // Create navigation items with useMemo to prevent recreating on every render
    const navigationItems = useMemo(() => (
        <div className="flex gap-4 text-sm">
            <Link
                to="/signatures/list"
                className={isListView ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
            >
                List View
            </Link>
            <Link
                to="/signatures/dashboard"
                className={isDashboardView ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
            >
                Dashboard
            </Link>
            <Link
                to="/signatures/analytics"
                className={isAnalyticsView ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
            >
                Analytics
            </Link>
            {viewingAllAccounts && (
                <Link
                    to="/signatures/report"
                    className={isReportView ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
                >
                    Report
                </Link>
            )}
        </div>
    ), [isListView, isDashboardView, isAnalyticsView, isReportView, viewingAllAccounts]);

    useEffect(() => {
        // Set the page header with title and navigation
        setPageHeader(copy.Services.signatures.title, navigationItems);

        // Clear the header when unmounting
        return () => setPageHeader(null);
    }, [navigationItems, setPageHeader, copy.Services.signatures.title]);

    if (isReportView && !viewingAllAccounts) {
        return <Navigate to="/signatures/list" replace />;
    }

    return (
        <div className="container-fixed">
            {isListView ? <SignaturesList /> : isDashboardView ? <SignaturesGraph /> : isReportView ? <SignaturesByAccount /> : <Analytics />}
        </div>
    );
}
